import React, { useMemo } from 'react';
import {
    VitalTranslationDomainKeys,
    VitalTranslationImplementation,
    VitalTranslationProvider,
} from '@modernhealth/vital-components';

import { useTranslation } from 'react-i18next';

/**
 * This file maps react-i18next's translation API onto the
 * Design System's translation provider. The APIs are
 * essentially 1:1, but the design system cannot directly
 * depend on react-i18next without copying string files
 * across repositories currently.
 *
 * Until we have a better translation setup for the design
 * system, this mapping layer is the temporary solution.
 */

const domainToTranslationKey: VitalTranslationDomainKeys = {
    datePicker: {
        dateIs: 'vita:inputs.dateTime.dateIs',
        previousMonth: 'vita:inputs.dateTime.previousMonth',
        nextMonth: 'vita:inputs.dateTime.nextMonth',
        month: 'vita:inputs.dateTime.month',
        year: 'vita:inputs.dateTime.year',
        closeWindow: 'vita:inputs.dateTime.closeWindow',
        calendarHeader: 'vita:inputs.dateTime.calendarHeader',
        buttonLabel: 'vita:inputs.dateTime.buttonLabel',
    },
    form: {
        errorHeading: 'vita:form.error_heading',
    },
    // TODO: update mapped values
    rating: {
        star: '{{rating}} stars',
        ratingDescription: '{{rating}} out of {{maxRating}} stars',
    },
};

// Freeze domainToTranslationKey
Object.freeze(domainToTranslationKey);

// And freeze all properties of domainToTranslationKey
Object.getOwnPropertyNames(domainToTranslationKey).map(propertyName =>
    Object.freeze(domainToTranslationKey[propertyName]),
);

export const DSTranslationProvider = ({ children }) => {
    const { i18n, t } = useTranslation();

    const implementation: VitalTranslationImplementation = useMemo(
        () => ({
            currentLocale: i18n.language,
            translationByKey(domain, key, fallback, values) {
                if (domain) {
                    const mappedKey: string = domainToTranslationKey[domain][key];

                    return t(mappedKey ?? fallback, { ...values });
                }
                return t(key ?? fallback, { ...values });
            },
        }),
        [i18n.language, t],
    );

    return <VitalTranslationProvider value={implementation}>{children}</VitalTranslationProvider>;
};
